/**
 * Color-coded 0..1 probability bar. Used by:
 *   - components/v2/member/ReplyProbabilityScore.tsx
 *   - components/communications/AudienceByLOBPanel.tsx (compact variant)
 */

import React from 'react';

interface Props {
  probability: number; // 0..1
  variant?: 'default' | 'compact';
  showLabel?: boolean;
}

function colorFor(p: number): string {
  if (p >= 0.5) return 'bg-emerald-500';
  if (p >= 0.2) return 'bg-amber-500';
  if (p >= 0.05) return 'bg-orange-500';
  return 'bg-rose-500';
}

function labelFor(p: number): string {
  if (p >= 0.5) return 'High';
  if (p >= 0.2) return 'Moderate';
  if (p >= 0.05) return 'Low';
  return 'Very low';
}

export const PredictionConfidenceBar: React.FC<Props> = ({
  probability,
  variant = 'default',
  showLabel = true,
}) => {
  const pct = Math.max(0, Math.min(1, probability)) * 100;
  const color = colorFor(probability);

  if (variant === 'compact') {
    return (
      <div className="inline-flex items-center gap-2">
        <div className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden">
          <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs tabular-nums text-gray-700">{pct.toFixed(1)}%</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        {showLabel && <span className="font-medium text-gray-900">{labelFor(probability)}</span>}
        <span className="tabular-nums text-gray-700">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
